<script setup lang="ts">
import { mdiDelete, mdiDiamondStone, mdiDotsVertical, mdiPencil, mdiPlay } from '@mdi/js'
import greenGrimoire from '@/assets/images/GreenCodex_256x256.webp'
import blueGrimoire from '@/assets/images/BlueCodex_256x256.webp'
import brownGrimoire from '@/assets/images/BrownCodex_256x256.webp'
import redGrimoire from '@/assets/images/RedCodex_256x256.webp'
import blackGrimoire from '@/assets/images/BlackCodex_256x256.webp'
import openedGrimoire from '@/assets/images/OpenGrimoire_367x256.webp'

interface Props {
  sceneId: string
  name: string
  type: string
  /**
   * バックエンドが返す project.readonly フラグ。true の場合この grimoire は
   * 編集不可（crystal / privilege / snapshot / 未クリア encodia / github 等）。
   */
  readonly?: boolean
  color?: string
  open?: boolean
  showCrystallize?: boolean
  showMenu?: boolean
}

const props = withDefaults(defineProps<Props>(), { readonly: false })
const emit = defineEmits([
  'click',
  // 編集可: Playground のエディタを開く
  'program',
  // 読み取り専用: Playground を viewer モードで開く
  'read',
  // 読み取り専用 / GitHub: コードを動かしてみる
  // (GitHub は entrypoint ダイアログ経由、それ以外は createProjectSession 経由)
  'play',
  'detail',
  'crystallize',
  'rename',
  'delete',
])

// type と readonly の組み合わせから 3 ボタンの可視性を決める。
// 詳細マトリクスは doc/domain/cradle.md「カード操作のマトリックス」参照。
const isGithub = computed(() => props.type === 'github')
const canProgram = computed(() => !props.readonly)
const canRead = computed(() => props.readonly && !isGithub.value)
const canPlay = computed(() => props.readonly)

const { isSceneUnlocked } = useSceneAccess()
const isUnlocked = computed(() => isSceneUnlocked(props.sceneId))

const codexSrc = computed(() => {
  const map: Record<string, string> = {
    green: greenGrimoire,
    blue: blueGrimoire,
    brown: brownGrimoire,
    red: redGrimoire,
    black: blackGrimoire,
  }
  return map[props.color ?? 'red']
})

const onClick = () => emit('click', props.sceneId)
const onDetail = () => emit('detail', props.sceneId)
const onProgram = () => emit('program', props.sceneId)
const onRead = () => emit('read', props.sceneId)
const onPlay = () => emit('play', props.sceneId)
const onCrystallize = () => emit('crystallize', props.sceneId)
const onRename = () => emit('rename', props.sceneId)
const onDelete = () => emit('delete', props.sceneId)
</script>

<template>
  <v-card
    class="grimoire"
    :ripple="false"
    @click="onClick"
  >
    <div class="grimoire-inner">
      <!-- Menu dots -->
      <div
        v-if="showMenu"
        class="icon-menu"
      >
        <v-menu>
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              :icon="mdiDotsVertical"
              density="compact"
              variant="text"
              size="small"
              @click.stop
            />
          </template>
          <v-list density="compact">
            <v-list-item
              :prepend-icon="mdiPencil"
              title="名前を変える"
              @click.stop="onRename"
            />
            <v-list-item
              :prepend-icon="mdiDelete"
              title="削除する"
              class="text-red"
              @click.stop="onDelete"
            />
          </v-list>
        </v-menu>
      </div>

      <!-- Grimoire image -->
      <template v-if="open">
        <div class="opened-wrapper">
          <v-img
            :lazy-src="openedGrimoire"
            :src="openedGrimoire"
            aspect-ratio="1"
            cover
            class="opened-image"
          />
        </div>
        <!-- Stack text and two buttons vertically -->
        <div class="overlay-text d-flex flex-column ga-4">
          <div class="overlay-name">
            <p>{{ name }}</p>
          </div>
          <div class="flex-grow-1">
            <v-btn
              variant="elevated"
              color="red-lighten-5"
              density="default"
              text="くわしく見る"
              class="w-100"
              @click.stop="onDetail"
            />
          </div>
          <div
            v-if="showCrystallize"
            class="flex-grow-1"
          >
            <v-btn
              variant="elevated"
              color="purple-lighten-4"
              density="default"
              class="w-100"
              @click.stop="onCrystallize"
            >
              <v-icon
                :icon="mdiDiamondStone"
                size="small"
                class="mr-1"
              />クリスタル化
            </v-btn>
          </div>
          <!-- 動かしてみる: readonly grimoire / GitHub black grimoire -->
          <div
            v-if="canPlay"
            class="flex-grow-1"
          >
            <v-btn
              variant="elevated"
              color="green-lighten-4"
              density="default"
              class="w-100"
              @click.stop="onPlay"
            >
              <v-icon
                :icon="mdiPlay"
                size="small"
                class="mr-1"
              />動かしてみる
            </v-btn>
          </div>
          <!-- コードを読む: readonly な non-github grimoire -->
          <div
            v-if="canRead"
            class="flex-grow-1"
          >
            <v-btn
              variant="elevated"
              color="blue-lighten-4"
              density="default"
              text="コードを読む"
              class="w-100"
              @click.stop="onRead"
            />
          </div>
          <!-- プログラム！: 編集可な grimoire (user / cleared encodia) -->
          <div
            v-if="canProgram"
            class="flex-grow-1"
          >
            <v-btn
              variant="elevated"
              color="blue-lighten-4"
              density="default"
              text="プログラム！"
              class="w-100"
              @click.stop="onProgram"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <v-img
          :lazy-src="codexSrc"
          :src="codexSrc"
          aspect-ratio="1"
          cover
          class="codex-image"
        />
        <div
          v-if="!isUnlocked"
          class="overlay-text"
        >
          <!-- <p>{{ conditionToUnlock }}</p> -->
          <p>{{ name }}</p>
        </div>
      </template>
    </div>
  </v-card>
</template>

<style scoped>
.grimoire {
  width: 100%;
  aspect-ratio: 7 / 8;

  background-color: #ffffffd0;
  box-shadow: 7px 7px 5px #00000060;
  /* padding: clamp(8.89px, 1.25dvw, 16px); */
  padding: max(8.89px, calc(1.25 * var(--u))) max(4.44px, calc(0.625 * var(--u)));

  cursor: pointer;
  user-select: none;
  position: relative;
  overflow: visible;
}

.grimoire-inner {
  position: relative;
  width: 100%;
  height: 100%;
  /* flex: 1; */

  overflow: visible;
}

.codex-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.opened-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 115%;                      /* ← 隣に干渉しないちょうど良い大きさ */
  height: 100%;
  transform: translate(-50%, -50%);
  overflow: visible;

  animation: openHorizontal 0.06s ease-out;
  transform-origin: center;
}

@keyframes openHorizontal {
  0%   { transform: translate(-50%, -50%) scaleX(0.3); opacity: 0; }
  100% { transform: translate(-50%, -50%) scaleX(1);   opacity: 1; }
}

.opened-image {
  width: 100%;
  /* height: auto; */
  aspect-ratio: 7 / 8;
}

.icon-menu {
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 10;
}

.overlay-text {
  position: absolute;
  top: 45%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -50%);
  /* color: white; */
  /* font-size: 2.0vh; */
  font-size: max(13.33px, calc(1.875 * var(--u)));
  font-weight: bold;
  /* text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.7); */
  /* pointer-events: none; */
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  color: #000;
  text-shadow:
    1px 1px 0 #fff,
    -1px -1px 0 #fff,
    -1px 1px 0 #fff,
    1px -1px 0 #fff,
    0px 1px 0 #fff,
    0-1px 0 #fff,
    -1px 0 0 #fff,
    1px 0 0 #fff;
}

.overlay-name {
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
